import { verifiedSocialLink } from '../../utils/verifiedSocialLink';
import { whatsappLink } from '../../utils/whatsappLink';
import React, { useState, useEffect } from 'react';
import '../../styles/layout/app-footer.css';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaPhoneAlt, FaFacebookF, FaInstagram, FaLinkedinIn, FaWhatsapp, FaHome, FaBoxOpen, FaBoxes, FaCouch, FaTruck, FaComments } from 'react-icons/fa';

const Footer = () => {
  const [siteSettings, setSiteSettings] = useState([]);

  useEffect(() => {
    let active = true;
    const fetchSettings = async () => {
      try {
        const res = await fetch('/api/site-settings');
        if (!res.ok) throw new Error('Failed to fetch settings');
        const data = await res.json();
        if (active) {
          setSiteSettings(data);
        }
      } catch (err) {
        console.error('Error fetching site settings for footer:', err);
      }
    };
    fetchSettings();
    return () => {
      active = false;
    };
  }, []);

  const getSetting = (key, defaultVal = '') => {
    const s = siteSettings.find(s => s.key === key && s.isActive);
    return s && s.value.trim() !== '' ? s.value : defaultVal;
  };

  const email = getSetting('support_email', 'support@flytteogfragttilbud.dk');
  const phone = getSetting('phone', '+45 35 95 55 94');

  const facebookUrl = verifiedSocialLink('facebook', getSetting('facebook_url'));
  const instagramUrl = verifiedSocialLink('instagram', getSetting('instagram_url'));
  const linkedinUrl = verifiedSocialLink('linkedin', getSetting('linkedin_url'));
  const whatsappUrl = whatsappLink(getSetting('whatsapp_url'));

  return (
    <footer className="app-footer w-full bg-[#0c3f56] text-[#b4d4e3] font-sans antialiased" style={{ backgroundColor: '#0c3f56', color: '#b4d4e3', fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
      <div className="app-footer__inner">

        {/* Main 4-Column Grid */}
        <div className="app-footer__grid">

          {/* 1. Branding & Social */}
          <div className="footer-column">
            <Link to="/" className="inline-flex items-center no-underline group" style={{ textDecoration: 'none' }}>
              <img
                src="/billeder/Transparent Logo.png"
                alt="Flytte- og Fragttilbud Logo"
                className="h-20 sm:h-24 w-auto object-contain"
                style={{ height: '95px', width: 'auto', maxWidth: '100%', objectFit: 'contain', display: 'block' }}
              />
            </Link>

            <p className="text-[13.5px] text-[#b4d4e3] leading-relaxed mt-3 mb-4 max-w-[270px]" style={{ fontSize: '13.5px', color: '#b4d4e3', lineHeight: 1.6, marginTop: '0.75rem', marginBottom: '1rem', maxWidth: '270px' }}>
              Din pålidelige platform for transport og flytteopgaver.
            </p>

            {/* Social Icons */}
            <div className="flex items-center gap-3" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {facebookUrl && <a
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/90 hover:bg-[#00a3e0] hover:text-white transition-all duration-200"
                style={{ textDecoration: 'none', width: '36px', height: '36px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <FaFacebookF size={15} />
              </a>}
              {instagramUrl && <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/90 hover:bg-[#00a3e0] hover:text-white transition-all duration-200"
                style={{ textDecoration: 'none', width: '36px', height: '36px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <FaInstagram size={16} />
              </a>}
              {!instagramUrl && <span role="img" aria-label="Instagram – profiladresse afventer" title="Profiladresse afventer" style={{width:36, height:36, borderRadius:'50%', backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center'}}><FaInstagram size={16} /></span>}
              {linkedinUrl && <a
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/90 hover:bg-[#00a3e0] hover:text-white transition-all duration-200"
                style={{ textDecoration: 'none', width: '36px', height: '36px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <FaLinkedinIn size={15} />
              </a>}
              {!linkedinUrl && <span role="img" aria-label="LinkedIn – profiladresse afventer" title="Profiladresse afventer" style={{width:36, height:36, borderRadius:'50%', backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center'}}><FaLinkedinIn size={15} /></span>}
            </div>
          </div>

          {/* 2. Services */}
          <div className="footer-column">
            <h3 className="app-footer__heading">Services</h3>
            <ul className="flex flex-col gap-3 text-[14px] text-[#b4d4e3] p-0 m-0 list-none" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '14px', color: '#b4d4e3' }}>
              <li>
                <Link to="/create-task?type=moving&category=normal" className="flex items-center gap-2.5 hover:text-white transition-colors group" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <FaHome className="text-[15px] opacity-80 group-hover:opacity-100 flex-shrink-0" style={{ fontSize: '15px' }} />
                  <span>Book flytning</span>
                </Link>
              </li>
              <li>
                <Link to="/create-task?type=courier&category=normal" className="flex items-center gap-2.5 hover:text-white transition-colors group" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <FaBoxOpen className="text-[15px] opacity-80 group-hover:opacity-100 flex-shrink-0" style={{ fontSize: '15px' }} />
                  <span>Book ekspres / kurér</span>
                </Link>
              </li>
              <li>
                <Link to="/create-task?type=package&category=normal" className="flex items-center gap-2.5 hover:text-white transition-colors group" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <FaBoxes className="text-[15px] opacity-80 group-hover:opacity-100 flex-shrink-0" style={{ fontSize: '15px' }} />
                  <span>Book pakker / paller</span>
                </Link>
              </li>
              <li>
                <Link to="/create-task?type=furniture&category=normal" className="flex items-center gap-2.5 hover:text-white transition-colors group" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <FaCouch className="text-[15px] opacity-80 group-hover:opacity-100 flex-shrink-0" style={{ fontSize: '15px' }} />
                  <span>Book møbeltransport</span>
                </Link>
              </li>
              <li>
                <Link to="/create-task?type=fullload&category=normal" className="flex items-center gap-2.5 hover:text-white transition-colors group" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <FaTruck className="text-[15px] opacity-80 group-hover:opacity-100 flex-shrink-0" style={{ fontSize: '15px' }} />
                  <span>Book hel lastbil</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* 3. Information (2 Columns with Separator) */}
          <div className="footer-column">
            <h3 className="app-footer__heading">Information</h3>
            <div className="grid grid-cols-2 divide-x divide-white/20 text-[14px] text-[#b4d4e3]" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', fontSize: '14px', color: '#b4d4e3' }}>
              <ul className="flex flex-col gap-3 p-0 m-0 pr-4 list-none" style={{ listStyle: 'none', padding: 0, margin: 0, paddingRight: '1rem', borderRight: '1px solid rgba(255,255,255,0.2)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <li><a href="/about" className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Om os</a></li>
                <li><a href="/how-it-works" className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Sådan virker det</a></li>
                <li><a href="/safety" className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Sikkerhed</a></li>
                <li><a href="/services" className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Services</a></li>
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Privatliv</Link></li>
              </ul>
              <ul className="flex flex-col gap-3 p-0 m-0 pl-4 list-none" style={{ listStyle: 'none', padding: 0, margin: 0, paddingLeft: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <li><a href="/help" className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Hjælp</a></li>
                <li><a href="/faq" className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>FAQ</a></li>
                <li><a href="/blog" className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Blog</a></li>
                <li><Link to="/terms-of-service" className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Vilkår</Link></li>
                <li><Link to="/carrier-agreement" className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Transportøraftale</Link></li>
              </ul>
            </div>
          </div>

          {/* 4. Kontakt os */}
          <div className="footer-column">
            <h3 className="app-footer__heading">Kontakt os</h3>
            <ul className="flex flex-col gap-3 text-[14px] text-[#b4d4e3] p-0 m-0 list-none" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '14px', color: '#b4d4e3' }}>
              <li>
                <a href={`mailto:${email}`} className="flex items-center gap-2.5 hover:text-white transition-colors group" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <FaEnvelope className="text-[14px] opacity-80 group-hover:opacity-100 flex-shrink-0" style={{ fontSize: '14px' }} />
                  <span className="app-footer__email">{email}</span>
                </a>
              </li>
              <li>
                <a href={`tel:${phone.replace(/\s+/g, '')}`} className="flex items-center gap-2.5 hover:text-white transition-colors group" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <FaPhoneAlt className="text-[14px] opacity-80 group-hover:opacity-100 flex-shrink-0" style={{ fontSize: '14px' }} />
                  <span>{phone}</span>
                </a>
              </li>

            </ul>

            {/* WhatsApp CTA Button with white bold icon and white bold text */}
            <div className="mt-5 flex flex-col gap-3" style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <Link
                to="/contact"
                className="flex items-center gap-2.5 text-[14px] text-[#b4d4e3] hover:text-white transition-colors group"
                style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '0.625rem', fontSize: '14px' }}
              >
                <FaComments className="text-[14px] opacity-80 group-hover:opacity-100 flex-shrink-0" style={{ fontSize: '14px' }} />
                <span>Kom i kontakt</span>
              </Link>
              {whatsappUrl && <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 bg-[#25D366] hover:bg-[#20bd5a] text-white font-extrabold text-[13.5px] py-2.5 px-5 rounded-full transition-all duration-200 shadow-sm hover:shadow group w-fit"
                style={{ textDecoration: 'none', backgroundColor: '#25D366', color: '#ffffff', fontWeight: 800, borderRadius: '9999px', display: 'inline-flex', alignItems: 'center', gap: '0.625rem', padding: '0.625rem 1.25rem', fontSize: '13.5px', width: 'fit-content' }}
              >
                <FaWhatsapp className="text-[20px] text-white flex-shrink-0" style={{ color: '#ffffff', fontSize: '20px' }} />
                <span style={{ color: '#ffffff', fontWeight: 800 }}>Skriv på WhatsApp &rarr;</span>
              </a>}
            </div>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="app-footer__bottom" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#90bfd4', flexWrap: 'wrap', gap: '1rem' }}>
          <p className="m-0" style={{ margin: 0 }}>&copy; {new Date().getFullYear()} Flytte- og Fragttilbud. Alle rettigheder forbeholdes.</p>

          {/* Payment Badges */}
          <div className="flex items-center gap-5" style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
            <span className="font-extrabold text-[#00a3e0] text-[16px] italic tracking-tight" style={{ color: '#00a3e0', fontWeight: 800, fontStyle: 'italic', fontSize: '16px', letterSpacing: '-0.025em' }}>MobilePay</span>
            <span className="font-extrabold text-white text-[15px] tracking-widest uppercase" style={{ color: '#ffffff', fontWeight: 800, letterSpacing: '0.1em', fontSize: '15px' }}>VISA</span>
            <div className="flex items-center gap-1.5" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <div className="flex items-center" style={{ display: 'flex', alignItems: 'center' }}>
                <div className="w-4 h-4 rounded-full bg-[#EB001B]" style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#EB001B' }}></div>
                <div className="w-4 h-4 rounded-full bg-[#F79E1B] -ml-2 opacity-95" style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#F79E1B', marginLeft: '-8px' }}></div>
              </div>
              <span className="font-medium text-white text-[13px] tracking-wide" style={{ color: '#ffffff', fontWeight: 500, fontSize: '13px', letterSpacing: '0.025em' }}>Mastercard</span>
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
